import type {
  AbsenceListParams,
  AbsenceRecord,
  AbsenceTypeListParams,
  AbsenceTypeRecord,
  CreateAbsencePayload,
  CreateAbsenceTypePayload,
  CursorPaginatedResponse,
  UpdateAbsencePayload,
  UpdateAbsenceTypePayload,
} from '@/libs/interfaces'

import { mockAbsences, mockAbsenceTypes } from '../fixtures/absences'
import { delay, genID, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const absenceTypeStore = createEntityStore<AbsenceTypeRecord, 'absenceTypeID'>(
  mockAbsenceTypes,
  'absenceTypeID',
)
const absenceStore = createEntityStore<AbsenceRecord, 'absenceID'>(mockAbsences, 'absenceID')

const filterAbsenceTypes = (params: AbsenceTypeListParams): AbsenceTypeRecord[] =>
  absenceTypeStore
    .getAll()
    .filter((absenceType) =>
      params.absenceTypeStatus ? absenceType.absenceTypeStatus === params.absenceTypeStatus : true,
    )

const filterAbsences = (
  params: Omit<AbsenceListParams, 'employeeID'> & { employeeID?: string },
): AbsenceRecord[] =>
  absenceStore
    .getAll()
    .filter((absence) => (params.employeeID ? absence.employeeID === params.employeeID : true))
    .filter((absence) => (params.status ? absence.status === params.status : true))
    .filter((absence) =>
      params.approvedById ? absence.approvedById === params.approvedById : true,
    )
    .filter((absence) => (params.rangeStart ? absence.endDateTime >= params.rangeStart : true))
    .filter((absence) => (params.rangeEnd ? absence.startDateTime <= params.rangeEnd : true))

export const absenceTypesApiMock = {
  create: async (orgID: string, payload: CreateAbsenceTypePayload): Promise<AbsenceTypeRecord> => {
    const absenceType: AbsenceTypeRecord = {
      absenceTypeID: genID('absence-type'),
      orgID,
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...payload,
    }
    absenceTypeStore.insert(absenceType)
    return delay(absenceType)
  },

  list: async (params: AbsenceTypeListParams): Promise<AbsenceTypeRecord[]> =>
    delay(filterAbsenceTypes(params)),

  listCursor: async (
    params: AbsenceTypeListParams,
  ): Promise<CursorPaginatedResponse<AbsenceTypeRecord>> =>
    delay(paginate(filterAbsenceTypes(params), params)),

  getByID: async (_orgID: string, absenceTypeID: string): Promise<AbsenceTypeRecord> =>
    delay(absenceTypeStore.getById(absenceTypeID) ?? ({} as AbsenceTypeRecord)),

  update: async (
    orgID: string,
    absenceTypeID: string,
    payload: UpdateAbsenceTypePayload,
  ): Promise<AbsenceTypeRecord> => {
    const updated = absenceTypeStore.update(absenceTypeID, {
      ...payload,
      orgID,
      updatedAt: nowISO(),
    })
    return delay(updated ?? ({ ...payload, absenceTypeID, orgID } as AbsenceTypeRecord))
  },

  delete: async (_orgID: string, absenceTypeID: string): Promise<AbsenceTypeRecord> => {
    const absenceType = absenceTypeStore.getById(absenceTypeID)
    absenceTypeStore.remove(absenceTypeID)
    return delay(absenceType ?? ({} as AbsenceTypeRecord))
  },
}

export const absencesApiMock = {
  create: async (
    orgID: string,
    employeeID: string,
    payload: CreateAbsencePayload,
  ): Promise<AbsenceRecord> => {
    const absence: AbsenceRecord = {
      absenceID: genID('absence'),
      orgID,
      employeeID,
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...payload,
    }
    absenceStore.insert(absence)
    return delay(absence)
  },

  list: async (params: AbsenceListParams): Promise<AbsenceRecord[]> =>
    delay(filterAbsences(params)),

  listAll: async (params: Omit<AbsenceListParams, 'employeeID'>): Promise<AbsenceRecord[]> =>
    delay(filterAbsences(params)),

  listCursor: async (params: AbsenceListParams): Promise<CursorPaginatedResponse<AbsenceRecord>> =>
    delay(paginate(filterAbsences(params), params)),

  getByID: async (_orgID: string, employeeID: string, absenceID: string): Promise<AbsenceRecord> =>
    delay(
      absenceStore.find(
        (item) => item.absenceID === absenceID && item.employeeID === employeeID,
      )[0] ?? ({} as AbsenceRecord),
    ),

  update: async (
    orgID: string,
    employeeID: string,
    absenceID: string,
    payload: UpdateAbsencePayload,
  ): Promise<AbsenceRecord> => {
    const updated = absenceStore.update(absenceID, {
      ...payload,
      orgID,
      employeeID,
      updatedAt: nowISO(),
    })
    return delay(updated ?? ({ ...payload, absenceID, orgID, employeeID } as AbsenceRecord))
  },

  delete: async (_orgID: string, absenceID: string): Promise<AbsenceRecord> => {
    const absence = absenceStore.getById(absenceID)
    absenceStore.remove(absenceID)
    return delay(absence ?? ({} as AbsenceRecord))
  },
}
